import { useEffect, useState } from "react";
import axios from "axios";
import { Play } from "lucide-react";
import { API } from "@/lib/adminApi";
import { AdRenderer } from "@/components/AdRenderer";

const device = () =>
  typeof window === "undefined" ? "desktop"
    : window.innerWidth < 640 ? "mobile"
      : window.innerWidth < 1024 ? "tablet" : "desktop";

const useServedAd = (placement, category = "", tool = "") => {
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/ads/serve`, { params: { placement, category, tool, device: device() } })
      .then((r) => { if (alive) setAd(r.data.ads?.[0] || null); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [placement, category, tool]);
  return { ad, loaded };
};

const AdShell = ({ children, label = "Advertisement", className = "", testid }) => (
  <div data-testid={testid} className={`relative border border-dashed border-border bg-muted ${className}`}>
    <span className="absolute left-2 top-1.5 overline text-muted-foreground">{label}</span>
    <div className="flex h-full w-full items-center justify-center">{children}</div>
  </div>
);

// Generic dynamic slot: renders a managed ad, else a subtle placeholder shell.
export const AdSlot = ({ placement, category = "", tool = "", className = "", shell }) => {
  const { ad, loaded } = useServedAd(placement, category, tool);
  if (ad) return <div data-testid={`adslot-${placement}`} className={className}>{<AdRenderer ad={ad} />}</div>;
  if (!loaded) return null;
  return shell || null;
};

export const BannerAd = ({ className = "", placement = "homepage-middle", category = "", tool = "" }) => (
  <AdSlot placement={placement} category={category} tool={tool} className={className}
    shell={
      <AdShell testid="ad-banner" className={`h-24 md:h-28 ${className}`}>
        <div className="text-center">
          <p className="font-heading text-sm font-bold text-muted-foreground">Leaderboard Ad · 728×90</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Manage in Admin → Advertisements</p>
        </div>
      </AdShell>
    } />
);

export const SidebarAd = ({ className = "", placement = "sidebar", category = "", tool = "" }) => (
  <AdSlot placement={placement} category={category} tool={tool} className={className}
    shell={
      <AdShell testid="ad-sidebar" className={`h-64 ${className}`}>
        <div className="px-4 text-center">
          <p className="font-heading text-sm font-bold text-muted-foreground">Sidebar Ad · 300×250</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Manage in Admin → Advertisements</p>
        </div>
      </AdShell>
    } />
);

export const VideoAd = ({ className = "", placement = "tool-page-middle" }) => (
  <AdSlot placement={placement} className={className}
    shell={
      <AdShell testid="ad-video" label="Video Advertisement" className={`aspect-video ${className}`}>
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <span className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-neutral-400">
            <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" />
          </span>
          <p className="font-heading text-sm font-bold">Video Ad Slot · 16:9</p>
        </div>
      </AdShell>
    } />
);

// Site-wide overlays managed entirely from the admin panel.
export const GlobalAds = () => {
  const announce = useServedAd("announcement-bar").ad;
  const footer = useServedAd("sticky-footer").ad;
  const floating = useServedAd("floating").ad;
  const sidebar = useServedAd("sticky-sidebar").ad;
  const popup = useServedAd("popup").ad;
  const [popupClosed, setPopupClosed] = useState(false);

  return (
    <>
      {announce && <AdRenderer ad={announce} />}
      {footer && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 p-2 backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-center"><AdRenderer ad={footer} /></div>
        </div>
      )}
      {sidebar && (
        <div className="fixed right-3 top-1/2 z-40 hidden -translate-y-1/2 lg:block"><AdRenderer ad={sidebar} /></div>
      )}
      {floating && (
        <div className="fixed bottom-4 right-4 z-40 max-w-xs rounded-xl border border-border bg-card p-2 shadow-lg"><AdRenderer ad={floating} /></div>
      )}
      {popup && !popupClosed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPopupClosed(true)}>
          <div className="relative max-w-lg rounded-2xl border border-border bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPopupClosed(true)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground">✕</button>
            <AdRenderer ad={popup} />
          </div>
        </div>
      )}
    </>
  );
};
